import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { Dashboard, PrebuiltSettings } from '@intly/core';
import { PrebuiltSettingDefinition } from './registry';

interface PrebuiltSettingFieldProps {
  setting: PrebuiltSettingDefinition;
  // Not set while the dashboard is being created
  dashboard?: Dashboard;
  // All settings as currently edited
  settings: PrebuiltSettings;
  onChange: (value: unknown) => void;
}

// One setting's editor with its description, as shown in the settings and create dialogs.
const PrebuiltSettingField: FC<PrebuiltSettingFieldProps> = ({
  setting,
  dashboard,
  settings,
  onChange,
}) => {
  const { t } = useTranslation();
  const value = settings[setting.key] ?? setting.defaultValue;

  return (
    <Box>
      {setting.type === 'custom' ? (
        <setting.editor
          dashboard={dashboard}
          settings={settings}
          label={t(setting.labelKey)}
          value={value}
          onChange={onChange}
        />
      ) : (
        <TextField
          select
          fullWidth
          size="small"
          label={t(setting.labelKey)}
          value={String(value)}
          onChange={(e) => onChange(e.target.value)}
        >
          {setting.options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {t(option.labelKey)}
              {option.value === setting.defaultValue && ` (${t('DEFAULT')})`}
            </MenuItem>
          ))}
        </TextField>
      )}
      {setting.descriptionKey && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.5 }}
        >
          {t(setting.descriptionKey)}
        </Typography>
      )}
    </Box>
  );
};

export default PrebuiltSettingField;
